if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();

        // Support both legacy (capitalised) and new (lowercase) IDs
        this.quantityInput =
          this.querySelector(`#quantity-input-${this.dataset.section}`) ||
          this.querySelector(`#Quantity-${this.dataset.section}`);
        this.quantityForm =
          this.querySelector(`#quantity-form-${this.dataset.section}`) ||
          this.querySelector(`#Quantity-Form-${this.dataset.section}`);
        this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));

        // Add subscription for option value selection change
        this.optionValueSelectionChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber();
        this.optionValueSelectionChangeUnsubscriber?.();
      }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    // Handle local variant selection for radio inputs or dropdown buttons
    // Use closest() to find the button even when clicking on child elements (like spans)
    const isRadio = event.target.type === 'radio';
    const variantButton = event.target.closest('[data-variant-option]');
    
    if (isRadio || variantButton) {
      this.handleLocalVariantSelection();
      return;
    }

    // For simple variant updates without product swapping
    this.updateVariants(selectedOptionValues);
  }

      handleLocalVariantSelection() {
        const variantSelects = this.querySelector('variant-selects');
        if (!variantSelects) return;

        const productDataScript = variantSelects.querySelector('[id^="ProductJSON-"]');
        if (!productDataScript) return;

        const productData = JSON.parse(productDataScript.textContent);
        
        // Get selected options from variantSelects component
        const selectedOptions = variantSelects.getSelectedOptions ? variantSelects.getSelectedOptions() : [];

        // Find matching variant - handle partial selections
        let selectedVariant = productData.find((variant) => {
          // Check if all selected options match the variant options
          return selectedOptions.every((selectedOption, index) => {
            return variant.options[index] === selectedOption;
          });
        });

        // If no exact match found, try to find a variant that matches the first option
        // This handles cases where only one option is selected
        if (!selectedVariant && selectedOptions.length > 0) {
          const firstSelectedOption = selectedOptions[0];
          selectedVariant = productData.find((variant) => {
            return variant.options[0] === firstSelectedOption && variant.available;
          });

          if (selectedVariant) {
            // Found variant matching first option
          }
        }

        if (selectedVariant) {
          this.updateVariantInputs(selectedVariant.id);
          this.updateURL(this.dataset.url, selectedVariant.id);
          this.updateProductForm(selectedVariant);

          // Update the data-selected-variant script
          const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
          if (selectedVariantScript) {
            selectedVariantScript.textContent = JSON.stringify(selectedVariant);
          }

          // Publish variant change event
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html: null,
              variant: selectedVariant,
            },
          });
        } else {
          this.setUnavailable();
        }
      }

      updateVariants(selectedOptionValues) {
        // This method is not needed - variant updates are handled by handleLocalVariantSelection
        // Remove this method to prevent it from overriding button state
      }

      handleVariantChange({ data: { variant } }) {
        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.pickupAvailability?.update(variant);
        this.updateProductForm(variant);

        // Ensure quantity rules reflect the selected variant
        this.fetchQuantityRules();
      }

      updateProductForm(variant) {
        const productForm = this.productForm;
        if (variant.available) {
          productForm?.toggleSubmitButton(false, window.variantStrings.addToCart);
        } else {
          productForm?.toggleSubmitButton(true, window.variantStrings.soldOut);
        }
      }

  updateVariantInputs(variantId) {
    this.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`;
        window.history.replaceState({}, '', newUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput?.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput?.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput?.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput?.dataset.step ? parseInt(this.quantityInput.dataset.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput?.setAttribute('min', min);

        if (max) {
          this.quantityInput?.setAttribute('max', max);
        } else {
          this.quantityInput?.removeAttribute('max');
        }
        this.quantityInput?.setAttribute('step', data.step);

        this.quantityForm?.classList.toggle('quantity--disabled', min === 0 && !this.quantityInput?.value);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(`#quantity-rules-cart-${this.dataset.section} .loading__spinner`)?.classList.remove(
          'hidden'
        );
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(`#quantity-rules-cart-${this.dataset.section} .loading__spinner`)?.classList.add(
              'hidden'
            )
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        // Updated server-rendered container (uses legacy capitalised ID)
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);

        if (quantityFormUpdated) {
          // Update the input (support both lowercase and legacy IDs)
          const updatedInput =
            quantityFormUpdated.querySelector(`#quantity-input-${sectionId}`) ||
            quantityFormUpdated.querySelector(`#Quantity-${sectionId}`);

          if (updatedInput) {
            this.quantityInput.setAttribute('data-cart-quantity', updatedInput.dataset.cartQuantity || 0);
            // Sync core attributes
            ['min', 'max', 'step'].forEach((attr) => {
              const val = updatedInput.getAttribute(attr);
              if (val !== null) {
                this.quantityInput.setAttribute(attr, val);
              } else {
                this.quantityInput.removeAttribute(attr);
              }
            });
            // Sync value and notify observers
            this.quantityInput.value = updatedInput.value;
            this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // Update the label content if present
          const currentLabel =
            this.querySelector(`#quantity-label-${sectionId}`) || this.querySelector(`#QuantityLabel-${sectionId}`);
          const updatedLabel =
            quantityFormUpdated.querySelector(`#quantity-label-${sectionId}`) ||
            quantityFormUpdated.querySelector(`#QuantityLabel-${sectionId}`);
          if (currentLabel && updatedLabel) {
            currentLabel.innerHTML = updatedLabel.innerHTML;
          }
        }

        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
      }

      get sectionId() {
        return this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector('pickup-availability');
      }
    }
  );
}
